import { Injectable } from "@angular/core";
import { DateAdapter } from "@angular/material/core";
import {
  DateRange,
  MatDatepickerIntl,
  MatDateRangeSelectionStrategy,
} from "@angular/material/datepicker";

/**
 * Cómo se marcan las fechas en el calendario de «Pedir vacaciones»: el
 * primer toque es el primer día y el segundo, el último. Tocar un día
 * anterior al primero, o tocar cuando ya están los dos, vuelve a empezar.
 * Un solo día: se toca dos veces.
 *
 * Es la regla de Material (DefaultMatCalendarRangeStrategy) menos el
 * arrastre: arrastrar un rango ya marcado sólo lo recibe el datepicker
 * emergente, y en el calendario en línea el rango se movía en pantalla y
 * al soltar volvía a su lugar.
 *
 * Desde el 13/10/2026 el paso enseña dos meses, cada uno en su calendario.
 * Material sólo dibuja el tramo que se va marcando en el mes que tiene el
 * puntero, así que un tramo de octubre a noviembre se veía cortado: quien
 * se apunte en `alPrevisualizar` lo recibe y lo pinta en los dos.
 */
@Injectable()
export class RangoVacaciones implements MatDateRangeSelectionStrategy<Date> {
  /** A quién avisarle del tramo que quedaría con el día bajo el puntero
   *  (vacío si no hay ninguno). Si alguien se apunta, Material ya no lo
   *  dibuja por su cuenta. */
  alPrevisualizar: ((tramo: DateRange<Date>) => void) | null = null;

  constructor(private adapter: DateAdapter<Date>) {}

  selectionFinished(fecha: Date | null, rango: DateRange<Date>): DateRange<Date> {
    const { start, end } = rango;
    if (fecha && start && !end && this.adapter.compareDate(fecha, start) >= 0) {
      return new DateRange<Date>(start, fecha);
    }
    return new DateRange<Date>(fecha, null);
  }

  /** Mientras falta el último día, el tramo se dibuja hasta donde está el
   *  puntero. Con días sueltos (o en un calendario de un solo día) no hay
   *  tramo que dibujar: lo elegido no es un DateRange. */
  createPreview(activa: Date | null, rango: DateRange<Date> | Date | null): DateRange<Date> {
    const tramo = this.tramoHasta(activa, rango);
    if (!this.alPrevisualizar) {
      return tramo;
    }
    this.alPrevisualizar(tramo);
    return new DateRange<Date>(null, null);
  }

  private tramoHasta(activa: Date | null, rango: DateRange<Date> | Date | null): DateRange<Date> {
    if (activa && rango instanceof DateRange && rango.start && !rango.end &&
        this.adapter.compareDate(activa, rango.start) >= 0) {
      return new DateRange<Date>(rango.start, activa);
    }
    return new DateRange<Date>(null, null);
  }
}

/**
 * Lo que el lector de pantalla dice del tramo que se va marcando. El paso
 * lo pinta con la «comparación» de Material, que de fábrica se anuncia en
 * inglés («Comparison range»).
 */
@Injectable()
export class EtiquetasCalendario extends MatDatepickerIntl {
  override comparisonDateLabel = "Tramo que se marcaría:";
}
